class CatalogPopup extends HTMLElement {
  constructor() {
    super();

    if (window.location.pathname === '/challenge') return;

    this.root = this.querySelector('[data-catalog-popup-root]');
    this.dialog = this.querySelector('[data-catalog-popup-dialog]');
    this.overlay = this.querySelector('[data-catalog-popup-overlay]');
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[data-catalog-popup-submit]');
    this.submitLabel = this.querySelector('[data-catalog-popup-submit-label]');
    this.loadingLabel = this.querySelector('[data-catalog-popup-loading]');
    this.success = this.querySelector('[data-catalog-popup-success]');
    this.error = this.querySelector('[data-catalog-popup-error]');
    this.nameInput = this.querySelector('[data-catalog-popup-name]');
    this.emailInput = this.querySelector('[data-catalog-popup-email]');
    this.rdNameInput = this.querySelector('[data-catalog-popup-rd-name]');
    this.rdEmailInput = this.querySelector('[data-catalog-popup-rd-email]');
    this.couponCode = this.dataset.couponCode || '';

    this.cookieName = `beyours:catalog-popup:${this.dataset.sectionId || 'default'}`;
    this.globalCookieName = `caleo:coupon-popup:${this.getConversionIdentifier() || 'popup_cupom_5_off'}`;
    this.storageName = `${this.globalCookieName}:submitted`;
    this.sessionName = `${this.globalCookieName}:session-completed`;
    this.globalCompletedFlagName = 'CaleoCouponPopupCompleted';
    this.classes = {
      body: 'catalog-popup--open',
      active: 'is-active',
      closing: 'is-closing',
      outsideClicked: 'is-outside-clicked'
    };

    this.isSubmitting = false;
    this.hasOpened = false;
    this.lastFocusedElement = null;
    this.closeTimer = null;
    this.outsideFeedbackTimer = null;
    this.triggerTimer = null;
    this.toastTimer = null;
    this.successfulSubmission = false;
    this.pointerStartedInsideDialog = false;

    this.querySelectorAll('[data-catalog-popup-close]').forEach((button) => {
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });

    this.onOverlayClickHandler = this.onOverlayClick.bind(this);

    if (this.overlay) {
      this.overlay.addEventListener('click', this.onOverlayClickHandler);
    }

    if (this.root) {
      this.root.addEventListener('click', this.onOverlayClickHandler);
    }

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('input', this.syncRDFields.bind(this));
    }

    this.onKeydownHandler = this.onKeydown.bind(this);
    this.onScrollHandler = this.onScroll.bind(this);
    this.onExitIntentHandler = this.onExitIntent.bind(this);
    this.onGlobalSuccessHandler = this.onGlobalSuccess.bind(this);
  }

  connectedCallback() {
    if (!this.root) return;

    document.addEventListener('caleo:rdstation-coupon:success', this.onGlobalSuccessHandler);

    if (window.Shopify && window.Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
      return;
    }

    if (this.dataset.testMode === 'true' || !this.isSuppressed()) {
      this.initTrigger();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydownHandler);
    window.removeEventListener('scroll', this.onScrollHandler);
    document.removeEventListener('mouseleave', this.onExitIntentHandler);
    document.removeEventListener('caleo:rdstation-coupon:success', this.onGlobalSuccessHandler);

    if (window.Shopify && window.Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }

    document.body.classList.remove(this.classes.body);
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.outsideFeedbackTimer);
    window.clearTimeout(this.triggerTimer);
  }

  onSectionLoad(event) {
    if (window.filterShopifyEvent) {
      filterShopifyEvent(event, this, this.open.bind(this));
    }
  }

  onSectionSelect(event) {
    if (window.filterShopifyEvent) {
      filterShopifyEvent(event, this, this.open.bind(this));
    }
  }

  onSectionDeselect(event) {
    if (window.filterShopifyEvent) {
      filterShopifyEvent(event, this, this.close.bind(this));
    }
  }

  initTrigger() {
    const trigger = this.dataset.trigger || 'delay_or_scroll';
    const delay = Math.max(parseInt(this.dataset.delay || '18', 10), 0);

    if (trigger === 'delay_or_scroll') {
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      this.onScroll();
      this.triggerTimer = window.setTimeout(() => {
        window.removeEventListener('scroll', this.onScrollHandler);
        this.openIfAllowed();
      }, delay * 1000);
      return;
    }

    if (trigger === 'scroll') {
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      this.onScroll();
      return;
    }

    if (trigger === 'exit_intent') {
      if (window.matchMedia('(pointer: fine)').matches) {
        document.addEventListener('mouseleave', this.onExitIntentHandler);
      } else {
        this.triggerTimer = window.setTimeout(() => this.openIfAllowed(), Math.max(delay, 12) * 1000);
      }
      return;
    }

    this.triggerTimer = window.setTimeout(() => this.openIfAllowed(), delay * 1000);
  }

  openIfAllowed() {
    if (this.hasCompletedGlobally()) return;
    this.open();
  }

  onGlobalSuccess(event) {
    if (this.isDispatchingSuccess) return;

    const conversionIdentifier = event?.detail?.conversion_identifier;
    const currentIdentifier = this.getConversionIdentifier();

    if (conversionIdentifier && currentIdentifier && conversionIdentifier !== currentIdentifier) return;

    this.successfulSubmission = true;
    window[this.globalCompletedFlagName] = true;
    this.setSuppression(this.dataset.successExpiry || this.dataset.expiry || 90);
    window.clearTimeout(this.triggerTimer);
    window.removeEventListener('scroll', this.onScrollHandler);
    document.removeEventListener('mouseleave', this.onExitIntentHandler);

    if (this.root && this.root.classList.contains(this.classes.active) && !this.root.classList.contains(this.classes.closing)) {
      this.close(this.dataset.successExpiry || this.dataset.expiry || 90);
    }
  }

  onScroll() {
    const depth = parseInt(this.dataset.scrollDepth || '45', 10);
    const scrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
    const current = (window.scrollY / scrollable) * 100;

    if (current >= depth) {
      window.removeEventListener('scroll', this.onScrollHandler);
      window.clearTimeout(this.triggerTimer);
      this.openIfAllowed();
    }
  }

  onExitIntent(event) {
    if (event.clientY <= 0) {
      document.removeEventListener('mouseleave', this.onExitIntentHandler);
      this.openIfAllowed();
    }
  }

  shouldCloseOnOutsideClick() {
    return this.dataset.closeOnOverlay !== 'false';
  }

  onOverlayClick(event) {
    if (!this.shouldCloseOnOutsideClick()) return;
    if (!this.root || !this.root.classList.contains(this.classes.active)) return;
    if (this.dialog && this.dialog.contains(event.target)) return;
    if (event.target !== this.overlay && event.target !== this.root) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeFromOutside();
  }

  closeFromOutside() {
    if (!this.root || this.root.classList.contains(this.classes.closing)) return;

    this.root.classList.add(this.classes.outsideClicked);
    window.clearTimeout(this.outsideFeedbackTimer);

    this.outsideFeedbackTimer = window.setTimeout(() => {
      if (this.root) {
        this.root.classList.remove(this.classes.outsideClicked);
      }
    }, 260);

    window.setTimeout(() => {
      this.close();
    }, 110);
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.dataset.closeOnEsc === 'true') {
      this.close();
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    if (!this.dialog) return;

    const selectors = 'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(this.dialog.querySelectorAll(selectors)).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open() {
    if (!this.root) return;
    if (this.successfulSubmission && this.dataset.testMode !== 'true') return;
    if (this.dataset.testMode !== 'true' && (this.isSuppressed() || this.hasCompletedGlobally())) return;
    if (this.hasOpened && this.dataset.testMode !== 'true') return;
    if (this.root.classList.contains(this.classes.active)) return;

    this.hasOpened = true;
    this.lastFocusedElement = document.activeElement;

    const promoPopup = document.querySelector('promo-popup');
    if (promoPopup && typeof promoPopup.close === 'function') {
      promoPopup.close();
    }

    const social = document.querySelector('social-floating');
    if (social && typeof social.close === 'function') {
      social.close();
    }

    if (typeof window.setScrollbarWidth === 'function') {
      window.setScrollbarWidth();
    } else {
      this.setScrollbarWidthFallback();
    }

    this.integrateRDForms();
    this.root.classList.remove('is-success');
    this.root.classList.add(this.classes.active);
    document.body.classList.add(this.classes.body);
    document.addEventListener('keydown', this.onKeydownHandler);

    window.setTimeout(() => {
      const canAutofocusInput = window.matchMedia && window.matchMedia('(pointer: fine)').matches && window.innerWidth >= 990;
      const firstInput = canAutofocusInput ? (this.nameInput || this.emailInput) : null;

      if (firstInput) {
        firstInput.focus({ preventScroll: true });
      } else if (this.dialog) {
        this.dialog.setAttribute('tabindex', '-1');
        this.dialog.focus({ preventScroll: true });
      }
    }, 140);
  }

  close(expiry) {
    if (!this.root || !this.root.classList.contains(this.classes.active) || this.root.classList.contains(this.classes.closing)) return;

    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.outsideFeedbackTimer);
    window.clearTimeout(this.triggerTimer);
    this.root.classList.add(this.classes.closing);
    this.root.classList.remove(this.classes.outsideClicked);
    document.removeEventListener('keydown', this.onKeydownHandler);

    window.setTimeout(() => {
      this.root.classList.remove(this.classes.active);
      this.root.classList.remove(this.classes.closing);
      this.root.classList.remove(this.classes.outsideClicked);
      this.root.classList.remove('is-success');
      document.body.classList.remove(this.classes.body);

      if (!this.successfulSubmission && this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus({ preventScroll: true });
      }

      if (this.successfulSubmission && this.dataset.testMode !== 'true') {
        this.remove();
      }
    }, 450);

    if (this.successfulSubmission) {
      this.setSuppression(expiry || this.dataset.successExpiry || this.dataset.expiry || 90);
      return;
    }

    if (this.dataset.testMode === 'true') {
      this.removeCookie(this.cookieName);
      return;
    }

    this.setCookie(this.cookieName, expiry || this.dataset.expiry || 15);
  }

  async onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.form || this.isSubmitting) return;
    if (!this.form.reportValidity()) return;

    this.syncRDFields();
    this.setLoading(true);
    this.hideError();
    window.CaleoCouponPopupSubmitting = true;

    try {
      await this.submitRD();
    } catch (error) {
      console.warn('Catalog popup RD Station integration failed:', error);

      window.CaleoCouponPopupSubmitting = false;
      this.setLoading(false);
      this.showError(error);
      return;
    }

    window.CaleoCouponPopupSubmitting = false;
    this.setLoading(false);
    this.showSuccess();
  }

  syncRDFields() {
    if (this.rdNameInput && this.nameInput) {
      this.rdNameInput.value = this.nameInput.value.trim();
    }

    if (this.rdEmailInput && this.emailInput) {
      this.rdEmailInput.value = this.emailInput.value.trim();
    }
  }

  async submitShopify() {
    return null;
  }

  async submitRD() {
    const mode = this.dataset.rdIntegrationMode || 'tracking_code';

    if (mode === 'disabled') return;

    const tasks = [];

    if (mode === 'tracking_code') {
      this.integrateRDForms();
      const rdLegacy = this.submitRDLegacy();
      if (rdLegacy) tasks.push(rdLegacy);
    }

    if (mode === 'webhook') {
      if (!this.dataset.rdWebhookUrl) {
        throw new Error('URL do webhook seguro não configurada.');
      }

      tasks.push(this.submitRDWebhook());
    }

    if (mode === 'custom_callback') {
      const customCallback = this.submitCustomRDCallback();
      if (customCallback) tasks.push(customCallback);
    }

    if (!tasks.length) return;

    const results = await Promise.allSettled(tasks);
    const hasError = results.some((result) => result.status === 'rejected');

    if (hasError) {
      const failed = results.find((result) => result.status === 'rejected');
      throw failed.reason || new Error('RD Station integration error');
    }
  }

  submitCustomRDCallback() {
    const callback = window.CaleoRDStationPopupSubmit || window.rdstationCouponPopupSubmit;

    if (typeof callback !== 'function') return null;

    return Promise.resolve(callback(this.getRDPayload(), this.serializeForm()));
  }

  submitRDLegacy() {
    if (!window.RdIntegration || typeof window.RdIntegration.post !== 'function') return null;

    const data = this.serializeForm();
    const token = this.dataset.rdPublicToken;

    if (token && !data.some((item) => item.name === 'token_rdstation' && item.value)) {
      data.push({ name: 'token_rdstation', value: token });
    }

    return new Promise((resolve, reject) => {
      let finished = false;
      const timeout = window.setTimeout(() => {
        if (!finished) {
          finished = true;
          resolve();
        }
      }, 4500);

      try {
        window.RdIntegration.post(data, () => {
          if (!finished) {
            finished = true;
            window.clearTimeout(timeout);
            resolve();
          }
        });
      } catch (error) {
        window.clearTimeout(timeout);
        reject(error);
      }
    });
  }

  async submitRDWebhook() {
    const payload = this.getRDPayload();
    const url = this.dataset.rdWebhookUrl;

    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Caleo-Event': this.getConversionIdentifier()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const responseText = await response.text().catch(() => '');
        throw new Error(responseText || 'Webhook response error');
      }

      return response.json().catch(() => ({ ok: true }));
    } catch (error) {
      throw error;
    }
  }

  integrateRDForms() {
    const token = this.dataset.rdPublicToken;

    if (!token) return;

    try {
      if (window.RdstationFormsIntegration && window.RdstationFormsIntegration.Integration && typeof window.RdstationFormsIntegration.Integration.integrateAll === 'function') {
        window.RdstationFormsIntegration.Integration.integrateAll(token);
      }
    } catch (error) {}
  }

  getConversionIdentifier() {
    if (this.form) {
      const input = this.form.querySelector('[name="conversion_identifier"], [name="identificador"]');
      if (input && input.value) return input.value.trim();
    }

    return this.dataset.conversionIdentifier || 'popup_cupom_5_off';
  }

  hasCompletedGlobally() {
    if (window[this.globalCompletedFlagName] === true) return true;

    try {
      if (window.sessionStorage.getItem(this.sessionName) === 'true') return true;
    } catch (error) {}

    if (this.getCookie(this.globalCookieName)) return true;

    try {
      const value = window.localStorage.getItem(this.storageName);
      if (!value) return false;

      const expiresAt = parseInt(value, 10);
      if (!expiresAt || expiresAt < Date.now()) {
        window.localStorage.removeItem(this.storageName);
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  isSuppressed() {
    if (this.hasCompletedGlobally()) return true;
    if (this.getCookie(this.cookieName) || this.getCookie(this.globalCookieName)) return true;

    try {
      const value = window.localStorage.getItem(this.storageName);
      if (!value) return false;

      const expiresAt = parseInt(value, 10);
      if (!expiresAt || expiresAt < Date.now()) {
        window.localStorage.removeItem(this.storageName);
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  setSuppression(expiry) {
    const days = parseInt(expiry || 90, 10);
    window[this.globalCompletedFlagName] = true;
    this.setCookie(this.cookieName, days);
    this.setCookie(this.globalCookieName, days);

    try {
      window.sessionStorage.setItem(this.sessionName, 'true');
    } catch (error) {}

    try {
      window.localStorage.setItem(this.storageName, String(Date.now() + days * 24 * 60 * 60 * 1000));
    } catch (error) {}
  }

  showToast() {
    const email = this.emailInput ? this.emailInput.value.trim() : '';
    const title = this.dataset.toastTitle || this.success?.querySelector('strong')?.textContent?.trim() || 'Cupom enviado!';
    const message = email
      ? `Enviamos o cupom para <strong class="catalog-popup-toast__email">${this.escapeHTML(email)}</strong>. Confira sua caixa de entrada ou a aba de promoções.`
      : this.escapeHTML(this.dataset.toastMessage || this.success?.querySelector('p')?.textContent?.trim() || 'Enviamos o cupom para o e-mail informado.');

    document.querySelectorAll('.catalog-popup-toast').forEach((toast) => toast.remove());

    const toast = document.createElement('div');
    toast.className = 'catalog-popup-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <div class="catalog-popup-toast__content">
        <strong>${this.escapeHTML(title)}</strong>
        <span>${message}</span>
      </div>
      <button class="catalog-popup-toast__close" type="button" aria-label="Fechar aviso">×</button>
    `;

    document.body.appendChild(toast);

    const closeButton = toast.querySelector('.catalog-popup-toast__close');
    const removeToast = () => {
      if (!toast || toast.classList.contains('is-hiding')) return;
      toast.classList.add('is-hiding');
      window.setTimeout(() => toast.remove(), 280);
    };

    if (closeButton) {
      closeButton.addEventListener('click', removeToast);
    }

    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(removeToast, 6200);
  }

  escapeHTML(value) {
    return String(value || '').replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    })[char]);
  }

  getRDPayload() {
    const conversionIdentifier = this.getConversionIdentifier();
    const destinationUrl = this.dataset.catalogUrl || '';
    const couponCode = this.form.querySelector('[name="cf_coupon_code"]')?.value || this.couponCode || '';
    const tags = this.form.querySelector('[name="contact[tags]"]')?.value || '';
    const name = this.nameInput ? this.nameInput.value.trim() : '';
    const email = this.emailInput ? this.emailInput.value.trim() : '';
    const honeypot = this.form.querySelector('[name="company_website"]')?.value || '';

    const normalizedTags = tags.split(',').map((tag) => tag.trim()).filter(Boolean);

    return {
      event_type: 'CONVERSION',
      event_family: 'CDP',
      conversion_identifier: conversionIdentifier,
      name,
      email,
      tags: normalizedTags,
      coupon_code: couponCode,
      source: 'shopify_theme',
      page_url: window.location.href,
      page_title: document.title,
      honeypot,
      payload: {
        conversion_identifier: conversionIdentifier,
        name,
        email,
        tags: normalizedTags,
        cf_coupon_code: couponCode,
        cf_popup_source: 'shopify_theme',
        cf_destination_url: destinationUrl,
        cf_source_url: window.location.href,
        traffic_source: window.location.href,
        cf_page_title: document.title,
        cf_client_timestamp: new Date().toISOString(),
        honeypot
      }
    };
  }

  serializeForm() {
    return Array.from(new FormData(this.form).entries()).map(([name, value]) => ({ name, value }));
  }

  showSuccess() {
    const successExpiry = this.dataset.successExpiry || this.dataset.expiry || 90;

    this.successfulSubmission = true;
    window.CaleoCouponPopupCompleted = true;
    if (this.root) this.root.classList.add('is-success');
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    window.clearTimeout(this.triggerTimer);
    this.hideError();
    this.setSuppression(successExpiry);
    this.isDispatchingSuccess = true;
    this.dispatchSuccessEvent();
    this.isDispatchingSuccess = false;
    this.showToast();

    if (this.form) this.form.hidden = true;
    if (this.success) {
      this.success.hidden = false;
      this.success.focus({ preventScroll: true });
    }

    if (this.dataset.openCatalogAfterSubmit === 'true' && this.dataset.catalogUrl) {
      window.setTimeout(() => {
        window.open(this.dataset.catalogUrl, '_blank', 'noopener');
      }, 600);
    }

    const closeDelay = parseInt(this.dataset.successCloseDelay || '0', 10);
    if (closeDelay > 0) {
      this.closeTimer = window.setTimeout(() => {
        this.close(successExpiry);
      }, closeDelay * 1000);
    }
  }

  dispatchSuccessEvent() {
    const detail = this.getRDPayload().payload;

    document.dispatchEvent(new CustomEvent('caleo:discount-popup:success', { detail }));
    document.dispatchEvent(new CustomEvent('caleo:rdstation-coupon:success', { detail }));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'popup_cupom_5_off',
      conversion_identifier: detail.conversion_identifier,
      email: detail.email,
      name: detail.name,
      coupon_code: detail.cf_coupon_code
    });
  }

  showError(error) {
    if (this.error) {
      const box = this.error.querySelector('.catalog-popup__error-box');
      if (box && error && error.message) {
        box.textContent = this.getFriendlyErrorMessage(error.message);
      }

      this.error.hidden = false;
      this.error.focus({ preventScroll: true });
    }
  }

  getFriendlyErrorMessage(message) {
    const value = String(message || '');

    if (value.includes('Origem não autorizada') || value.includes('403')) {
      return 'Não foi possível validar este envio. Revise os domínios permitidos do webhook.';
    }

    if (value.includes('RD_API_KEY') || value.includes('token privado') || value.includes('Unauthorized') || value.includes('401') || value.includes('403')) {
      return 'Não foi possível enviar o cadastro para o RD Station. Revise o token configurado no webhook/Worker.';
    }

    if (value.includes('E-mail inválido')) {
      return 'Confira o e-mail informado e tente novamente.';
    }

    return 'Não foi possível concluir o cadastro agora. Tente novamente em alguns instantes.';
  }

  hideError() {
    if (this.error) {
      this.error.hidden = true;
    }
  }

  setLoading(isLoading) {
    this.isSubmitting = isLoading;

    if (this.submitButton) {
      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (this.submitLabel && this.loadingLabel) {
      this.submitLabel.hidden = isLoading;
      this.loadingLabel.hidden = !isLoading;
    }
  }

  setScrollbarWidthFallback() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    const maxAge = parseInt(expiry || 15, 10) * 24 * 60 * 60;
    document.cookie = `${name}=true; max-age=${maxAge}; path=/; SameSite=Lax`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
  }
}

if (!customElements.get('catalog-popup')) {
  customElements.define('catalog-popup', CatalogPopup);
}